import { useCallback, useEffect, useRef, useState } from "react";
import {
  canNavigateAway,
  leavesCurrentRoute,
  registerGuard,
} from "@/lib/navigationGuard";

const SENTINEL_KEY = "navigationGuardSentinel";

interface UseNavigationGuardOptions {
  enabled: boolean;
  message?: string;
}

interface NavigationGuardState {
  active: boolean;
  reject: () => void;
}

/**
 * Blocks the user from leaving the current page while `enabled`.
 *
 * Coverage, in order of how much control the browser gives us:
 * - In-app navigation — every `useRouter().push`/`replace` in the app, via
 *   `NavigationGuardProvider`. Call sites need no code of their own.
 * - Anchor clicks — intercepted here at the capture phase, which is what
 *   covers `<Link>`: it navigates through Next's internal dispatcher rather
 *   than the router this hook can see.
 * - Back/forward — `popstate`, with a sentinel history entry so the event
 *   fires at all.
 * - Tab close, reload, typing a new URL — `beforeunload`, which can only
 *   ever produce the browser's own generic dialog.
 *
 * Leaving via a full page load (`window.location.href`) is the one path
 * nothing here can veto; those call sites check `canNavigateAway()` directly
 * so they get this dialog rather than the browser's.
 *
 * The guard is a hard block: there is no "leave anyway" path, so the only
 * action the dialog offers is dismissal.
 *
 * @returns `active` — whether the dialog should be open — and `reject` to
 * dismiss it.
 */
export function useNavigationGuard({
  enabled,
  message = "You have unsaved changes. Are you sure you want to leave?",
}: UseNavigationGuardOptions): NavigationGuardState {
  const [active, setActive] = useState(false);
  const historyPushedRef = useRef(false);

  const reject = useCallback(() => setActive(false), []);

  // Publish this page's veto to every navigation call site in the app.
  useEffect(() => {
    if (!enabled) {
      setActive(false);
      return;
    }

    return registerGuard({
      blocks: (href) => href === undefined || leavesCurrentRoute(href),
      onBlocked: () => setActive(true),
    });
  }, [enabled]);

  // Tab close, reload, external URLs
  useEffect(() => {
    if (!enabled) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Modern browsers ignore the custom message and show their own
      e.returnValue = message;
      return message;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [enabled, message]);

  // Anchor clicks
  useEffect(() => {
    if (!enabled) return;

    const handleClick = (e: MouseEvent) => {
      // A modified click opens a new tab or window, so this page survives it.
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        return;
      }
      let target = e.target as HTMLElement | null;
      while (target && target !== document.body) {
        if (target.tagName === "A") {
          const href = target.getAttribute("href");
          // Neither a new tab nor a mail/phone handler unloads this page.
          const opensNewTab = target.getAttribute("target") === "_blank";
          const handedToOs = !!href && /^(mailto|tel|sms):/i.test(href);
          if (href && !opensNewTab && !handedToOs && !canNavigateAway(href)) {
            e.preventDefault();
            e.stopPropagation();
          }
          break;
        }
        target = target.parentElement;
      }
    };

    // Capture phase, so we run before Next's own link handling.
    document.addEventListener("click", handleClick, true);

    return () => {
      document.removeEventListener("click", handleClick, true);
    };
  }, [enabled]);

  // Back/forward buttons
  useEffect(() => {
    // Tagged so we can tell our own entry from one Next pushed after us.
    // Spread the existing state so the router's own bookkeeping survives.
    const pushSentinel = () =>
      window.history.pushState(
        { ...window.history.state, [SENTINEL_KEY]: true },
        "",
        window.location.href,
      );

    const handlePopState = () => {
      // Undo the pop so the user stays put, which also restores the
      // sentinel entry for the next attempt.
      pushSentinel();
      canNavigateAway();
    };

    const retireSentinel = () => {
      window.removeEventListener("popstate", handlePopState);
      // Leaving it behind would cost the user a dead Back press per
      // recording. Skip it if a later navigation already replaced it.
      if (
        historyPushedRef.current &&
        window.history.state?.[SENTINEL_KEY] === true
      ) {
        window.history.back();
      }
      historyPushedRef.current = false;
    };

    if (!enabled) {
      retireSentinel();
      return;
    }

    // popstate only fires if there is an entry to pop back to.
    if (!historyPushedRef.current) {
      pushSentinel();
      historyPushedRef.current = true;
    }

    window.addEventListener("popstate", handlePopState);

    return retireSentinel;
  }, [enabled]);

  return {
    active,
    reject,
  };
}
